import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, MoveLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button, buttonVariants } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';
import { PhoneInput } from 'components/ui/phone-input';
import { Separator } from 'components/ui/separator';
import useAuthStore from 'store/auth';
import { SignUpFormData, SignUpValidator } from 'lib/validators/sign-up';
import { useSignUpV2 } from 'services/auth/hook';
import { SignUpFlowType } from 'services/auth/type';
import { encryptText } from 'lib/kms';
import useAuthenticate from 'hooks/useAuthenticate';
import { ScrollArea } from 'components/ui/scroll-area';

export default function SignUpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { onAuthenticate } = useAuthenticate();

  const setSignupEmail = useAuthStore((store) => store.setSignupEmail);
  const setSignupPhone = useAuthStore((store) => store.setSignupPhone);
  const setSignUpUserId = useAuthStore((store) => store.setSignupUserId);
  const resetSignUp = useAuthStore((store) => store.resetSignUp);
  const setSignupPassword = useAuthStore((store) => store.setSignupPassword);
  const setEmailVerified = useAuthStore((store) => store.setEmailVerified);
  const setPhoneVerified = useAuthStore((store) => store.setPhoneVerified);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    resetSignUp();
  }, []);

  const { mutate } = useSignUpV2({
    onSuccess: (res, { data: { username, password, phone_number }, hsbcFlow }) => {
      if (!res?.phone_verification_flag || !res?.email_verification_flag || hsbcFlow) {
        setSignupEmail(username);
        setSignupPassword(password!);
        setSignupPhone(phone_number!);
        setSignUpUserId(res?.user_id || '');
        setEmailVerified(res?.email_verification_flag || false);
        setPhoneVerified(res?.phone_verification_flag || false);
        setIsLoading(false);
        navigate(hsbcFlow ? PATH_ROUTES.HSBC_VERIFICATION : PATH_ROUTES.SIGNUP_VERIFICATION);
        return;
      }

      onAuthenticate({
        email: username,
        password: password!,
        setLoading: setIsLoading,
      });
    },
    onError: () => setIsLoading(false),
  });

  const form = useForm<SignUpFormData>({
    resolver: zodResolver(SignUpValidator),
    defaultValues: {
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
  });

  async function onSubmit(values: SignUpFormData) {
    setIsLoading(true);
    const { email, phone, password } = values;

    try {
      const encryptedPassword = await encryptText(password);
      mutate({
        flowType: SignUpFlowType.SIGN_UP,
        data: {
          username: email.toLowerCase(),
          phone_number: phone,
          password: encryptedPassword,
        },
        hsbcFlow: false,
      });
    } catch (error) {
      toast.error(t('failed_account_create_msg'));
      setIsLoading(false);
    }
  }

  return (
    <ScrollArea className='w-full h-screen'>
      <div className='w-full py-10'>
        <div className='absolute hidden lg:block top-4 left-4'>
          <Link
            to={PATH_ROUTES.LOGIN}
            className={buttonVariants({
              variant: 'ghost',
              className: 'rounded-md',
            })}
          >
            <MoveLeft className='w-5 h-5' />
          </Link>
        </div>

        <div className='flex flex-col items-center w-full space-y-8'>
          <div className='w-4/5 mx-auto sm:w-2/5'>
            <h2 className='text-2xl font-semibold leading-none tracking-tight text-center transition-colors text-primary'>
              {t('share_your_details')}
            </h2>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className='w-full px-8 space-y-8 sm:w-2/3'
            >
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('email')}</FormLabel>

                    <FormControl>
                      <Input
                        {...field}
                        inputMode='email'
                        autoCapitalize='none'
                        spellCheck={false}
                        value={(field.value ?? '').toLowerCase()}
                        onChange={(e) =>
                          field.onChange(e.target.value.toLowerCase())
                        }
                        onPaste={(e) => {
                          e.preventDefault();
                          field.onChange(
                            e.clipboardData.getData('text').toLowerCase(),
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='phone'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('phone')}</FormLabel>
                    <FormControl>
                      <PhoneInput defaultCountry='MX' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor='password'>{t('password')}</FormLabel>
                    <FormControl>
                      <div className='relative'>
                        <Input
                          className='pr-10'
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          id='password'
                        />
                        <Button
                          type='button'
                          variant='ghost'
                          className='absolute right-0 rounded-md -top-1'
                          onClick={() => setShowPassword((prev) => !prev)}
                          data-testid='toggle-password'
                        >
                          {showPassword ? (
                            <Eye className='w-5 h-5 text-primary' />
                          ) : (
                            <EyeOff className='w-5 h-5 text-primary' />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='confirmPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor='confirm-password'>
                      {t('confirm_password')}
                    </FormLabel>
                    <FormControl>
                      <div className='relative'>
                        <Input
                          className='pr-10'
                          {...field}
                          type={showConfirmPassword ? 'text' : 'password'}
                          id='confirm-password'
                        />
                        <Button
                          type='button'
                          variant='ghost'
                          className='absolute right-0 rounded-md -top-1'
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          data-testid='toggle-confirm-password'
                        >
                          {showConfirmPassword ? (
                            <Eye className='w-5 h-5 text-primary' />
                          ) : (
                            <EyeOff className='w-5 h-5 text-primary' />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='flex justify-center !mt-10'>
                <Button
                  type='submit'
                  className='px-10 font-semibold rounded-full'
                  disabled={!form.formState.isValid || isLoading}
                >
                  {isLoading && (
                    <Loader2
                      role='progressbar'
                      className='w-4 h-4 mr-2 animate-spin'
                    />
                  )}{' '}
                  {t('sign_up')}
                </Button>
              </div>
            </form>
          </Form>

          <div className='flex flex-col items-center justify-center'>
            <div className='flex items-center gap-x-2'>
              <a
                href={process.env.REACT_APP_MEDSI_TOC}
                target='_blank'
                rel='noopener noreferrer'
                className={buttonVariants({
                  variant: 'link',
                  className: 'cursor-pointer',
                })}
              >
                {t('tnc')}
              </a>
              <Separator orientation='vertical' className='h-4' />
              <a
                href={process.env.REACT_APP_MEDSI_PRIVACY}
                target='_blank'
                rel='noopener noreferrer'
                className={buttonVariants({
                  variant: 'link',
                  className: 'cursor-pointer',
                })}
              >
                {t('pp')}
              </a>
            </div>

            <div>
              <span className='font-medium'>{t('have_acount')}</span>{' '}
              <Link
                to={PATH_ROUTES.LOGIN}
                className={buttonVariants({ variant: 'link' })}
              >
                {t('sign_in')}
              </Link>
            </div>
          </div>
        </div>
      </div>

    </ScrollArea>
  );
}
